import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import api from '../../services/api';
import { FiUser, FiGitBranch, FiPackage, FiSettings, FiRadio } from 'react-icons/fi';

const categoryIcons: Record<string, { icon: React.ReactNode; color: string }> = {
  persona: { icon: <FiUser className="w-3 h-3" />, color: 'text-purple-400' },
  workflow: { icon: <FiGitBranch className="w-3 h-3" />, color: 'text-blue-400' },
  artifactory: { icon: <FiPackage className="w-3 h-3" />, color: 'text-cyan-400' },
  gymnasium: { icon: <FiSettings className="w-3 h-3" />, color: 'text-amber-400' },
  pulse: { icon: <FiRadio className="w-3 h-3" />, color: 'text-indigo-400' },
  system: { icon: <FiSettings className="w-3 h-3" />, color: 'text-gray-400' },
};

const categoryBorders: Record<string, string> = {
  persona: 'border-l-2 border-purple-500',
  workflow: 'border-l-2 border-blue-500',
  artifactory: 'border-l-2 border-cyan-500',
  gymnasium: 'border-l-2 border-amber-500',
  pulse: 'border-l-2 border-indigo-500/30',
  system: 'border-l-2 border-gray-500',
};

const categoryHoverBg: Record<string, string> = {
  persona: 'hover:bg-purple-500/5',
  workflow: 'hover:bg-blue-500/5',
  artifactory: 'hover:bg-cyan-500/5',
  gymnasium: 'hover:bg-amber-500/5',
  pulse: 'hover:bg-indigo-500/10',
  system: 'hover:bg-gray-500/5',
};

function formatEventDescription(e: any): string {
  const meta = e.event_metadata || {};
  const name = meta.persona_name || meta.workflow_name || meta.project_name || '';
  const typeMap: Record<string, string> = {
    'persona.genesis_completed': `New persona generated${meta.duration_ms ? ` in ${(meta.duration_ms / 1000).toFixed(1)}s` : ''}`,
    'persona.compile_completed': `${name} compiled${meta.duration_ms ? ` in ${(meta.duration_ms / 1000).toFixed(1)}s` : ''}`,
    'persona.published': `${name} published${meta.is_new ? ' (new)' : ''}`,
    'persona.remixed': `${meta.new_persona_name || 'Persona'} remixed from ${meta.source_persona_name || name}`,
    'persona.capabilities_extracted': `${name} capabilities extracted (${meta.capability_count || '?'} entries)`,
    'persona.probe_run_completed': `${name} probes generated (${meta.probe_count || '?'} probes)`,
    'persona.probe_passed': `${name} probe passed: ${meta.probe_label || ''}`,
    'persona.probe_failed': `${name} probe failed: ${meta.probe_label || ''}`,
    'persona.deleted': `${meta.persona_name || 'Persona'} deleted`,
    'workflow.created': `Workflow "${name}" created`,
    'workflow.execution_started': `Workflow "${name}" execution started`,
    'workflow.execution_completed': `Workflow "${name}" ${meta.status || 'completed'}`,
    'workflow.phase_started': `Phase "${meta.phase_label || '?'}" started`,
    'workflow.phase_completed': `Phase "${meta.phase_label || '?'}" ${meta.status || 'done'}${meta.duration_ms ? ` (${(meta.duration_ms / 1000).toFixed(1)}s)` : ''}`,
    'workflow.probe_suggestions_emitted': `Probe suggestions emitted for ${meta.persona_count || '?'} personas`,
    'artifactory.manifest_created': `Project scaffolded`,
    'artifactory.node_hydration_completed': `${meta.node_path || 'Node'} hydrated${meta.duration_ms ? ` in ${(meta.duration_ms / 1000).toFixed(1)}s` : ''}`,
    'artifactory.export_triggered': `Project "${meta.project_name || ''}" exported`,
    'artifactory.handoff_triggered': `Project "${meta.project_name || ''}" handed off`,
    'system.suggestion_accepted': `Suggestion accepted`,
    'system.suggestion_dismissed': `Suggestion dismissed`,
    'system.cross_system_send': `Cross-system send: ${meta.action || ''}`,
    'gymnasium.match_started': `Arena match: ${meta.persona_a || '?'} vs ${meta.persona_b || '?'}`,
    'gymnasium.match_completed': `Arena: ${meta.winner_name || meta.winner || 'tie'} won — ${meta.persona_a || ''} vs ${meta.persona_b || ''}`,
    'gymnasium.gauntlet_run_started': `Gauntlet "${meta.gauntlet_name || ''}": ${name} starting (${meta.scenario_count || '?'} scenarios)`,
    'gymnasium.gauntlet_run_completed': `Gauntlet "${meta.gauntlet_name || ''}": ${name} scored ${meta.pass_rate != null ? (meta.pass_rate * 100).toFixed(0) + '%' : '?'}`,
    'gymnasium.ranking_updated': `${meta.persona_name || ''} Elo ${meta.delta > 0 ? '+' : ''}${meta.delta || 0} → ${meta.new_elo || '?'}`,
    'pulse.heartbeat.generated': 'Daily Heartbeat generated',
    'pulse.heartbeat.failed': 'Heartbeat failed',
  };
  return typeMap[e.event_type] || e.event_type.replace(/\./g, ' ');
}

function getRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getNavigationUrl(e: any): string | null {
  if (e.target_type === 'persona') return `/dashboards/personas/${e.target_id}`;
  if (e.target_type === 'execution' || e.target_type === 'workflow') return `/dashboards/workflows/${e.target_id}`;
  if (e.target_type === 'manifest' || e.target_type === 'node') return `/dashboards/artifactory/${e.event_metadata?.manifest_id || e.target_id}`;
  if (e.target_type === 'artifact' && e.event_category === 'pulse') return '/heartbeat';
  return null;
}

const ActivityFeed: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [events, setEvents] = useState<any[]>([]);

  useEffect(() => {
    api.get('/analytics/cross-system/events?limit=20')
      .then(r => setEvents(r.data.events || []))
      .catch(console.error);
  }, []);

  if (events.length === 0) {
    return <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No recent activity</p>;
  }

  return (
    <div className="space-y-1 max-h-[400px] overflow-y-auto">
      {events.map(e => {
        const cat = categoryIcons[e.event_category] || categoryIcons.system;
        const url = getNavigationUrl(e);
        const personaName = e.event_metadata?.persona_name;
        const personaId = e.target_type === 'persona' ? e.target_id : null;

        return (
          <div
            key={e.id}
            onClick={() => url && navigate(url)}
            className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${categoryBorders[e.event_category] || categoryBorders.system} ${url ? 'cursor-pointer' : ''} ${
              categoryHoverBg[e.event_category] || (darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100')
            }`}
          >
            <span className={`mt-1 ${cat.color}`}>{cat.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatEventDescription(e)}
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {getRelativeTime(e.created_at)}
              </p>
            </div>
            {personaName && (
              <PersonaBadge personaId={personaId || ''} personaName={personaName} size="xs" clickable={false} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActivityFeed;
